import React, { useCallback, useState } from "react";
import {
  getBusinessLogoUrl,
  insertApplication,
  uploadBusinessLogo,
} from "../services/api";
import { Application, BlankFormState, FormState, Status } from "../types";
import { InputField } from "./common/InputField";

const RegistrationForm: React.FC = () => {
  const [form, setForm] = useState<FormState>(BlankFormState);
  const [preview, setPreview] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState<ModalType>(null);
  const [modalMessage, setModalMessage] = useState<string>("");

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    setForm((s) => ({ ...s, businessLogo: file || null }));
    if (file) setPreview(URL.createObjectURL(file));
    else setPreview(null);
  };

  const validate = (form: FormState): string | null => {
    if (!form.studentId) return "La matrícula es obligatoria.";
    if (!form.fullName.trim()) return "El nombre completo es obligatorio.";
    if (!form.businessName.trim())
      return "El nombre del emprendimiento es obligatorio.";
    if (!form.businessLogo)
      return "El logotipo del emprendimiento es obligatorio.";
    if (!form.studentEmail.trim())
      return "El correo electrónico es obligatorio.";
    if (!form.studentFaculty.trim()) return "La licenciatura es obligatoria.";
    if (!form.businessCategory.trim())
      return "La categoría del emprendimiento es obligatoria.";
    if (!form.businessURL.trim())
      return "El enlace del emprendimiento es obligatorio.";
    if (form.businessCategory === "Otros" && !form.specificCategory?.trim())
      return "La categoría del emprendimiento es obligatoria.";
    return null;
  };

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError(null);
      const v = validate(form);
      if (v) {
        setError(v);
        return;
      }

      setSubmitting(true);
      try {
        const successUpload = await uploadBusinessLogo(form.businessLogo!);
        if (!successUpload) throw new Error("Error al subir el logo.");

        const logoUrl = await getBusinessLogoUrl(form.businessLogo!.name);
        const application: Application = {
          studentId: form.studentId,
          studentEmail: form.studentEmail,
          fullName: form.fullName,
          businessName: form.businessName,
          businessLogo: logoUrl,
          studentFaculty: form.studentFaculty,
          businessCategory: form.businessCategory,
          businessURL: form.businessURL,
          specificCategory: form.specificCategory,
          registrationDate: new Date().toISOString(),
          status: Status.Pending,
        };

        await insertApplication(application);

        setShowModal("success");
        setModalMessage(
          "¡Tu registro ha sido enviado exitosamente! El equipo de administración revisará tu solicitud en las próximas 48 horas."
        );

        setForm(BlankFormState);
        setPreview(null);
      } catch (err: unknown) {
        setShowModal("error");
        if (err instanceof Error && err.message === "23505") {
          setModalMessage(
            "Ya existe una solicitud registrada con esta matrícula, Por favor contacta con el administrador para solicitar cambios en el registro."
          );
        } else {
          setModalMessage(
            "Ocurrió un error al enviar tu solicitud. Por favor, verifica tu conexión e intenta nuevamente."
          );
        }
      } finally {
        setSubmitting(false);
      }
    },
    [form]
  );

  const closeModal = () => {
    setShowModal(null);
    setModalMessage("");
  };

  type ModalType = "success" | "error" | null;

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-5" id="registrationForm">
        {error && <div className="text-sm text-red-600">{error}</div>}

        <div className="grid grid-cols-1 gap-4">
          <InputField
            id="fullName"
            name="fullName"
            label="Nombre completo *"
            type="text"
            // tooltip="Escribe tu nombre completo tal como aparece en los registros escolares o en tu identificación oficial."
            value={form.fullName}
            onChange={(e) => setForm({ ...form, fullName: e.target.value })}
          />

          <InputField
            id="studentEmail"
            name="studentEmail"
            label="Correo electrónico *"
            type="email"
            // tooltip="Introduce tu correo institucional o personal activo. A este correo se enviará la confirmación de tu registro y documentos oficiales."
            value={form.studentEmail}
            onChange={(e) => setForm({ ...form, studentEmail: e.target.value })}
          />

          <InputField
            id="studentId"
            name="studentId"
            label="Matrícula (sin 'a') *"
            type="number"
            placeholder="2183810186"
            // tooltip="Escribe únicamente los números de tu matrícula, sin incluir la letra ‘a’. Ejemplo: 2183810186."
            value={form.studentId}
            onChange={(e) => setForm({ ...form, studentId: e.target.value })}
          />

          <div className="relative">
            <label
              htmlFor="studentFaculty"
              className="block font-medium text-[var(--uat-gray)] mb-2"
            >
              Licenciatura *
            </label>
            <select
              name="studentFaculty"
              id="studentFaculty"
              className="w-full rounded-lg bg-white/60 border border-gray-200 px-4 py-3 text-sm outline-none focus:border-[var(--uat-blue)]"
              value={form.studentFaculty}
              onChange={(e) => {
                setForm({ ...form, studentFaculty: e.target.value });
              }}
            >
              <option value="">Selecciona tu licenciatura</option>
              <option value="Bachillerato">Bachillerato</option>
              <option value="Licenciatura en Mercadotecnia y Administración">
                Licenciatura en Mercadotecnia y Administración
              </option>
              <option value="Licenciatura en Educación y Tecnologías para el Aprendizaje">
                Licenciatura en Educación y Tecnologías para el Aprendizaje
              </option>
              <option value="Licenciatura en Tecnología Educativa">
                Licenciatura en Tecnología Educativa
              </option>
            </select>
          </div>

          <InputField
            id="businessName"
            name="businessName"
            label="Nombre del emprendimiento *"
            type="text"
            // tooltip="Introduce el nombre oficial con el que registrarás tu emprendimiento. Aparecerá en tu carta de aprobación y cartel."
            value={form.businessName}
            onChange={(e) => setForm({ ...form, businessName: e.target.value })}
          />

          <div className="relative">
            <label
              htmlFor="businessCategory"
              className="block font-medium text-[var(--uat-gray)] mb-2"
            >
              Categoría del emprendimiento *
            </label>
            <select
              name="businessCategory"
              id="businessCategory"
              className="w-full rounded-lg bg-white/60 border border-gray-200 px-4 py-3 text-sm outline-none focus:border-[var(--uat-blue)]"
              value={form.businessCategory}
              onChange={(e) => {
                setForm({ ...form, businessCategory: e.target.value });
              }}
            >
              <option value="">Selecciona tu categoría</option>
              <option value="Alimentos">Alimentos</option>
              <option value="Accesorios">Accesorios</option>
              <option value="Ropa">Ropa</option>
              <option value="Servicios">Servicios</option>
              <option value="Otros">Otros</option>
            </select>
          </div>

          {form.businessCategory === "Otros" && (
            <InputField
              id="specificCategory"
              name="specificCategory"
              label="Especifica la categoría *"
              type="text"
              // tooltip="Si elegiste ‘Otros’, describe brevemente la categoría que se ajusta mejor a tu proyecto."
              value={form.specificCategory!}
              onChange={(e) =>
                setForm({ ...form, specificCategory: e.target.value })
              }
            />
          )}

          <InputField
            id="businessURL"
            name="businessURL"
            label="Link del emprendimiento (Facebook o Instagram) *"
            type="text"
            // tooltip="Pega el enlace directo a la página o cuenta oficial de tu emprendimiento. Asegúrate de que sea pública."
            value={form.businessURL}
            placeholder="https://www.instagram.com/..."
            onChange={(e) => setForm({ ...form, businessURL: e.target.value })}
          />
        </div>

        <div className="space-y-2">
          <label className="block font-medium text-[var(--uat-gray)] mb-2">
            Logo del emprendimiento *
          </label>
          <div className="flex flex-col gap-4">
            <label className="relative group cursor-pointer">
              <input
                type="file"
                accept="image/png, image/jpeg"
                onChange={handleFile}
                className="hidden"
                id="businessLogo"
                name="businessLogo"
              />
              <div className="flex items-center gap-3 px-4 py-3 rounded-lg border-2 border-dashed border-gray-200 bg-gray-50/50 group-hover:border-[var(--uat-accent)] group-hover:bg-gray-50/80 transition-colors duration-200">
                <div className="p-2 rounded-full bg-[var(--uat-accent)]/10 text-[var(--uat-accent)]">
                  📁
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-[var(--uat-gray)]">
                    {form.businessLogo
                      ? form.businessLogo.name
                      : "Seleccionar archivo"}
                  </p>
                  <p className="text-xs text-[var(--uat-gray)]/70">
                    PNG o JPG (max. 2MB)
                  </p>
                </div>
              </div>
            </label>

            {preview && (
              <div className="relative group">
                <img
                  src={preview}
                  alt="Vista previa"
                  className="h-32 w-auto rounded-lg border-2 border-gray-200 shadow-sm"
                />
                <button
                  type="button"
                  onClick={() => {
                    setForm({ ...form, businessLogo: null });
                    setPreview(null);
                  }}
                  className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 transition"
                >
                  ✕
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-4 flex-col md:flex-row">
          <button
            type="submit"
            disabled={submitting}
            className="relative inline-flex items-center justify-center px-6 py-3 rounded-full text-sm font-semibold text-black
                   bg-[var(--uat-gold)] shadow-lg shadow-[var(--uat-accent)]/20
                   transition-all duration-200 transform hover:-translate-y-0.5 hover:shadow-xl 
                   hover:shadow-[var(--uat-accent)]/30 active:translate-y-0 disabled:opacity-60 w-full md:w-auto"
          >
            <span className="absolute inset-0 rounded-full opacity-20 bg-white/20" />
            {submitting ? "Enviando..." : "Registrar"}
          </button>

          <button
            type="button"
            onClick={() => {
              setForm(BlankFormState);
              setPreview(null);
              setError(null);
            }}
            className="text-sm text-[var(--uat-gray)] hover:underline"
          >
            Limpiar
          </button>
        </div>
      </form>
      {showModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 animate-fadeIn space-y-0">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full animate-slideUp">
            {/* Header del Modal */}
            <div
              className={`rounded-t-2xl px-6 py-8 text-center ${
                showModal === "success"
                  ? "bg-gradient-to-br from-green-500 to-green-600"
                  : "bg-gradient-to-br from-red-500 to-red-600"
              }`}
            >
              <div className="inline-flex items-center justify-center w-20 h-20 bg-white rounded-full mb-4 shadow-lg">
                <span className="text-5xl">
                  {showModal === "success" ? "✓" : "✕"}
                </span>
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">
                {showModal === "success"
                  ? "¡Registro Exitoso!"
                  : "Error en el Registro"}
              </h3>
            </div>

            {/* Contenido del Modal */}
            <div className="p-6">
              <p className="text-gray-700 text-center leading-relaxed">
                {modalMessage}
              </p>
            </div>

            {/* Footer del Modal */}
            <div className="bg-white px-6 py-4 rounded-b-2xl flex justify-center gap-3">
              {showModal === "success" ? (
                <button
                  onClick={closeModal}
                  className="px-8 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-semibold shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                >
                  ¡Entendido!
                </button>
              ) : (
                <>
                  <button
                    onClick={closeModal}
                    className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition font-medium"
                  >
                    Cerrar
                  </button>
                  <button
                    onClick={() => {
                      closeModal();
                    }}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-semibold"
                  >
                    Reintentar
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slideUp {
          from {
            transform: translateY(20px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }

        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

export default RegistrationForm;
